import { PageHeading } from "@/components/ui/page";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Users, UserCheck, Building2, Shield, Plus, Edit, Trash2, Eye, Search, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { usePermission } from "@/hooks/usePermission";
import { Loading } from "@/components/ui/loader";
import { useToast } from "@/hooks/use-toast";
import { usersAPI, rolesAPI, departmentsAPI, permissionsAPI, getApiErrorMessage } from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";
import { GroupedPermissionsPanel } from "@/components/permissions/GroupedPermissionsPanel";
import {
	getPermissionModuleKey,
	getWorkflowGroupForPermissionType,
	PERMISSION_GROUP_IDS,
	type PermissionGroupId,
} from "@/lib/permission-groups";
import { getPermissionDisplayLabel } from "@/lib/permission-label";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { visibleEmployeeEmail } from "@/lib/employee-email";

type HubType = "users" | "roles" | "departments" | "permissions";

const TYPE_STYLES: Record<HubType, { icon: React.ElementType; active: string; dot: string }> = {
	users: {
		icon: Users,
		active: "border-primary/25 bg-primary/10 text-primary",
		dot: "bg-indigo-500",
	},
	roles: {
		icon: UserCheck,
		active: "border-warning/25 bg-warning/10 text-warning",
		dot: "bg-amber-500",
	},
	departments: {
		icon: Building2,
		active: "border-success/25 bg-success/10 text-success",
		dot: "bg-emerald-500",
	},
	permissions: {
		icon: Shield,
		active: "border-sky-300 bg-sky-50 text-sky-800",
		dot: "bg-sky-500",
	},
};

const TYPE_PERMISSIONS: Record<HubType, string> = {
	users: "users.read",
	roles: "roles.read",
	departments: "departments.read",
	permissions: "roles.read",
};

interface Employee {
	id: string;
	fullName: string;
	email: string;
	phone: string;
	photo?: string;
	status: "active" | "inactive" | "on_leave";
	role?: { id: string; name: string };
	branch?: { id: string; name: string };
}

interface Role {
	id: string;
	name: string;
	nameEn?: string;
	description?: string;
	descriptionEn?: string;
	status: "active" | "inactive";
	users?: { id: string }[];
	department?: { id: string; name: string };
	permissions?: Array<{ id: string; name: string }>;
	createdAt: string;
}

interface CreateRoleDto {
	name: string;
	nameEn?: string;
	description?: string;
	descriptionEn?: string;
	status: "active" | "inactive";
	departmentId?: string;
	permissionIds?: string[];
}

const EMPTY_ROLE_FORM: CreateRoleDto = {
	name: "",
	nameEn: "",
	description: "",
	descriptionEn: "",
	status: "active",
	departmentId: undefined,
	permissionIds: [],
};

interface Department {
	id: string;
	name: string;
	description?: string;
	status: "active" | "inactive";
	departmentHead?: string;
	createdAt: string;
	roles: { id: string }[];
}

interface CreateDepartmentDto {
	name: string;
	description?: string;
	status: "active" | "inactive";
	departmentHead?: string;
}

const EMPTY_DEPARTMENT_FORM: CreateDepartmentDto = {
	name: "",
	description: "",
	status: "active",
	departmentHead: "",
};

interface Permission {
	id: number;
	name: string;
	description?: string;
	type: string;
	action: string;
	isActive: boolean;
	createdAt: string;
	countUsers?: number;
}

/**
 * Employees, roles, departments and the permission catalog merged into one
 * hub. The hierarchy runs department → roles (each carrying permissions) →
 * employees, and each view links to the next level.
 */
export default function ManagementHubPage() {
	const { t } = useTranslation();
	const { toast } = useToast();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const canUsers = usePermission(TYPE_PERMISSIONS.users);
	const canRoles = usePermission(TYPE_PERMISSIONS.roles);
	const canDepartments = usePermission(TYPE_PERMISSIONS.departments);
	const canCreateUsers = usePermission("users.create");
	const canCreateRoles = usePermission("roles.create");
	const canCreateDepartments = usePermission("departments.create");

	const visibleTypes = useMemo<HubType[]>(() => {
		const types: HubType[] = [];
		if (canUsers) types.push("users");
		if (canRoles) types.push("roles");
		if (canDepartments) types.push("departments");
		if (canRoles) types.push("permissions");
		return types;
	}, [canUsers, canRoles, canDepartments]);

	const paramType = searchParams.get("type") as HubType | null;
	const [type, setType] = useState<HubType>(
		paramType && visibleTypes.includes(paramType) ? paramType : (visibleTypes[0] ?? "users"),
	);
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [roleFilter, setRoleFilter] = useState("all");
	const [workflowGroupFilter, setWorkflowGroupFilter] = useState("all");

	const switchType = (next: HubType) => {
		setType(next);
		setSearch("");
		setStatusFilter("all");
		setRoleFilter("all");
		setWorkflowGroupFilter("all");
		setSearchParams(
			prev => {
				const p = new URLSearchParams(prev);
				p.set("type", next);
				return p;
			},
			{ replace: true },
		);
	};

	// --- Employees ---
	const [employees, setEmployees] = useState<Employee[]>([]);
	const [employeesLoading, setEmployeesLoading] = useState(canUsers);

	// --- Roles ---
	const [roles, setRoles] = useState<Role[]>([]);
	const [rolesLoading, setRolesLoading] = useState(canRoles);
	const [roleDialogOpen, setRoleDialogOpen] = useState(false);
	const [editingRole, setEditingRole] = useState<Role | null>(null);
	const [roleReadOnly, setRoleReadOnly] = useState(false);
	const [roleForm, setRoleForm] = useState<CreateRoleDto>(EMPTY_ROLE_FORM);
	const [roleSaving, setRoleSaving] = useState(false);

	// --- Departments ---
	const [departments, setDepartments] = useState<Department[]>([]);
	const [departmentsLoading, setDepartmentsLoading] = useState(canDepartments);
	const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
	const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
	const [departmentForm, setDepartmentForm] = useState<CreateDepartmentDto>(EMPTY_DEPARTMENT_FORM);
	const [departmentSaving, setDepartmentSaving] = useState(false);

	// --- Permissions catalog ---
	const [permissions, setPermissions] = useState<Permission[]>([]);
	const [permissionsLoading, setPermissionsLoading] = useState(canRoles);
	const [catalogWritesAllowed, setCatalogWritesAllowed] = useState<boolean | null>(null);

	const fetchEmployees = useCallback(async () => {
		if (!canUsers) return;
		try {
			setEmployeesLoading(true);
			const response = await usersAPI.getAll({ page: 1, limit: 100 });
			const usersData = response?.users || (Array.isArray(response) ? response : []);
			const mapped: Employee[] = usersData.map((emp: any) => ({
				id: emp.id,
				fullName: emp.fullName || [emp.firstName, emp.lastName].filter(Boolean).join(" "),
				email: emp.email || "",
				phone: emp.phone || "",
				photo: emp.photo
					? emp.photo.startsWith("http")
						? emp.photo
						: `${process.env.REACT_APP_API_URL?.replace("/api", "") || "http://localhost:3000"}${emp.photo}`
					: undefined,
				status: emp.status,
				role: emp.role,
				branch: emp.branch,
			}));
			setEmployees(mapped);
		} catch {
			toast({
				title: t("common.error"),
				description: t("employees.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setEmployeesLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canUsers]);

	const fetchRoles = useCallback(async () => {
		if (!canRoles) return;
		try {
			setRolesLoading(true);
			const response = await rolesAPI.getAll();
			setRoles(response);
		} catch {
			toast({
				title: t("common.error"),
				description: t("roles.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setRolesLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canRoles]);

	const fetchDepartments = useCallback(async () => {
		if (!canDepartments && !canRoles) return;
		try {
			setDepartmentsLoading(true);
			const response = await departmentsAPI.getAll();
			setDepartments(response);
		} catch {
			toast({
				title: t("common.error"),
				description: t("departments.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setDepartmentsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canDepartments, canRoles]);

	const fetchPermissions = useCallback(async () => {
		if (!canRoles) return;
		try {
			setPermissionsLoading(true);
			const [meta, response] = await Promise.all([
				permissionsAPI.getCatalogMeta().catch(() => null),
				permissionsAPI.getAll(),
			]);
			if (meta) setCatalogWritesAllowed(meta.catalogWritesAllowed);
			setPermissions(response);
		} catch {
			toast({
				title: t("common.error"),
				description: t("permissions.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setPermissionsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canRoles]);

	useEffect(() => {
		fetchEmployees();
	}, [fetchEmployees]);
	useEffect(() => {
		fetchRoles();
	}, [fetchRoles]);
	useEffect(() => {
		fetchDepartments();
	}, [fetchDepartments]);
	useEffect(() => {
		fetchPermissions();
	}, [fetchPermissions]);

	// --- Employee handlers ---
	const deleteEmployee = async (id: number | string) => {
		if (!(await confirmAction(t("employees.confirmations.delete"), { confirmLabel: t("common.delete") }))) return;
		try {
			await usersAPI.delete(id);
			toast({
				title: t("employees.messages.deleteSuccess"),
				description: t("employees.messages.deleteSuccessDescription"),
			});
			fetchEmployees();
		} catch {
			toast({
				title: t("employees.messages.deleteError"),
				description: t("employees.messages.deleteErrorDescription"),
				variant: "destructive",
			});
		}
	};

	// --- Role handlers ---
	const openRoleCreate = () => {
		setEditingRole(null);
		setRoleReadOnly(false);
		setRoleForm({ ...EMPTY_ROLE_FORM, permissionIds: [] });
		setRoleDialogOpen(true);
	};

	const openRoleDialog = (role: Role, readOnly: boolean) => {
		setEditingRole(role);
		setRoleReadOnly(readOnly);
		setRoleForm({
			name: role.name,
			nameEn: role.nameEn || "",
			description: role.description || "",
			descriptionEn: role.descriptionEn || "",
			status: role.status,
			departmentId: role.department?.id || undefined,
			permissionIds: role.permissions?.map(p => p.id) || [],
		});
		setRoleDialogOpen(true);
	};

	const saveRole = async () => {
		if (!roleForm.name.trim() || roleSaving) return;
		const payload = {
			name: roleForm.name,
			nameEn: roleForm.nameEn || undefined,
			description: roleForm.description || undefined,
			descriptionEn: roleForm.descriptionEn || undefined,
			status: roleForm.status,
			departmentId: roleForm.departmentId ? roleForm.departmentId : null,
			permissionIds: roleForm.permissionIds || [],
		};
		try {
			setRoleSaving(true);
			if (editingRole) {
				await rolesAPI.update(editingRole.id, payload);
				toast({ title: t("common.success"), description: t("roles.messages.updated") });
			} else {
				await rolesAPI.create(payload);
				toast({ title: t("common.success"), description: t("roles.messages.created") });
			}
			setRoleDialogOpen(false);
			setEditingRole(null);
			setRoleForm({ ...EMPTY_ROLE_FORM });
			fetchRoles();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(
					error,
					t,
					t(editingRole ? "roles.errors.updateFailed" : "roles.errors.createFailed"),
				),
				variant: "destructive",
			});
		} finally {
			setRoleSaving(false);
		}
	};

	const deleteRole = async (id: number | string) => {
		if (!(await confirmAction(t("roles.confirmations.delete"), { confirmLabel: t("common.delete") }))) return;
		try {
			await rolesAPI.delete(id);
			toast({ title: t("common.success"), description: t("roles.messages.deleted") });
			fetchRoles();
		} catch {
			toast({
				title: t("common.error"),
				description: t("roles.errors.deleteFailed"),
				variant: "destructive",
			});
		}
	};

	// --- Department handlers ---
	const openDepartmentCreate = () => {
		setEditingDepartment(null);
		setDepartmentForm({ ...EMPTY_DEPARTMENT_FORM });
		setDepartmentDialogOpen(true);
	};

	const openDepartmentEdit = (department: Department) => {
		setEditingDepartment(department);
		setDepartmentForm({
			name: department.name,
			description: department.description || "",
			status: department.status,
			departmentHead: department.departmentHead || "",
		});
		setDepartmentDialogOpen(true);
	};

	const saveDepartment = async () => {
		if (!departmentForm.name.trim() || departmentSaving) return;
		try {
			setDepartmentSaving(true);
			if (editingDepartment) {
				await departmentsAPI.update(editingDepartment.id, departmentForm);
				toast({ title: t("common.success"), description: t("departments.messages.updated") });
			} else {
				await departmentsAPI.create(departmentForm);
				toast({ title: t("common.success"), description: t("departments.messages.created") });
			}
			setDepartmentDialogOpen(false);
			setEditingDepartment(null);
			setDepartmentForm({ ...EMPTY_DEPARTMENT_FORM });
			fetchDepartments();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(
					error,
					t,
					t(editingDepartment ? "departments.errors.updateFailed" : "departments.errors.createFailed"),
				),
				variant: "destructive",
			});
		} finally {
			setDepartmentSaving(false);
		}
	};

	const deleteDepartment = async (id: number | string) => {
		if (!(await confirmAction(t("departments.confirmations.delete"), { confirmLabel: t("common.delete") }))) return;
		try {
			await departmentsAPI.delete(id);
			toast({ title: t("common.success"), description: t("departments.messages.deleted") });
			fetchDepartments();
		} catch {
			toast({
				title: t("common.error"),
				description: t("departments.errors.deleteFailed"),
				variant: "destructive",
			});
		}
	};

	// --- Helpers ---
	const employeeStatusBadge = (status: string) => {
		const config: Record<string, { variant: string; text: string }> = {
			active: { variant: "success", text: t("employees.status.active") },
			inactive: { variant: "warning", text: t("employees.status.inactive") },
			on_leave: { variant: "default", text: t("employees.status.onLeave") },
		};
		const c = config[status] ?? { variant: "secondary", text: status };
		return <Badge variant={c.variant as any}>{c.text}</Badge>;
	};

	const filteredEmployees = useMemo(() => {
		const q = search.trim().toLowerCase();
		return employees.filter(emp => {
			if (
				q &&
				!emp.fullName.toLowerCase().includes(q) &&
				!visibleEmployeeEmail(emp.email).toLowerCase().includes(q) &&
				!emp.phone.includes(q)
			)
				return false;
			if (statusFilter !== "all" && emp.status !== statusFilter) return false;
			if (roleFilter !== "all" && String(emp.role?.id) !== roleFilter) return false;
			return true;
		});
	}, [employees, search, statusFilter, roleFilter]);

	const filteredRoles = useMemo(() => {
		const q = search.trim().toLowerCase();
		return roles.filter(role => {
			if (
				q &&
				!role.name.toLowerCase().includes(q) &&
				!(role.nameEn ?? "").toLowerCase().includes(q) &&
				!(role.description ?? "").toLowerCase().includes(q)
			)
				return false;
			if (statusFilter !== "all" && role.status !== statusFilter) return false;
			return true;
		});
	}, [roles, search, statusFilter]);

	const filteredDepartments = useMemo(() => {
		const q = search.trim().toLowerCase();
		return departments.filter(dept => {
			if (q && !dept.name.toLowerCase().includes(q)) return false;
			if (statusFilter !== "all" && dept.status !== statusFilter) return false;
			return true;
		});
	}, [departments, search, statusFilter]);

	const isFiltering = search.trim() !== "" || statusFilter !== "all" || roleFilter !== "all";

	const filteredPermissions = useMemo(() => {
		const q = search.trim().toLowerCase();
		return permissions.filter(permission => {
			const label = getPermissionDisplayLabel(t, permission).toLowerCase();
			const matchesSearch =
				!q ||
				permission.name.toLowerCase().includes(q) ||
				(permission.description?.toLowerCase().includes(q) ?? false) ||
				label.includes(q);
			const moduleKey = getPermissionModuleKey(permission);
			const wf = getWorkflowGroupForPermissionType(moduleKey);
			const matchesWorkflow = workflowGroupFilter === "all" || wf === (workflowGroupFilter as PermissionGroupId);
			return matchesSearch && matchesWorkflow;
		});
	}, [permissions, search, workflowGroupFilter, t]);

	const typeLabels: Record<HubType, string> = {
		users: t("navigation.users"),
		roles: t("navigation.roles"),
		departments: t("navigation.departments"),
		permissions: t("navigation.permissions"),
	};

	const contentLoading =
		(type === "users" && employeesLoading) ||
		(type === "roles" && rolesLoading) ||
		(type === "departments" && departmentsLoading) ||
		(type === "permissions" && permissionsLoading);

	return (
		<div className="space-y-4 p-4 md:p-6">
			<div>
				<PageHeading className="mb-1 ">{t("navigation.administration")}</PageHeading>
				<p className="text-muted-foreground text-sm">{t("managementHub.description")}</p>
			</div>

			{/* Unified toolbar: type chips + contextual filters + contextual action */}
			<div className="flex flex-wrap items-center gap-2 rounded-xl border bg-card p-2">
				{visibleTypes.map(key => {
					const style = TYPE_STYLES[key];
					const Icon = style.icon;
					const isSelected = type === key;
					return (
						<button
							key={key}
							aria-pressed={isSelected}
							type="button"
							onClick={() => switchType(key)}
							className={cn(
								"flex min-h-11 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
								isSelected
									? "border-transparent bg-secondary text-primary"
									: "border-transparent text-muted-foreground hover:bg-muted/60",
							)}
						>
							<Icon className="h-4 w-4" />
							{typeLabels[key]}{" "}
							<span className="text-xs tabular-nums">
								{
									{
										users: employees.length,
										roles: roles.length,
										departments: departments.length,
										permissions: permissions.length,
									}[key]
								}
							</span>
						</button>
					);
				})}

				<div className="ms-auto flex flex-wrap items-center gap-2">
					<div className="relative w-full sm:w-auto">
						<Search className="absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							value={search}
							onChange={e => setSearch(e.target.value)}
							placeholder={
								type === "users"
									? t("employees.searchPlaceholder")
									: type === "roles"
										? t("roles.filters.searchPlaceholder")
										: type === "departments"
											? t("departments.fields.namePlaceholder")
											: t("permissions.filters.searchPlaceholder")
							}
							className="h-9 w-full sm:w-52 ps-8"
						/>
					</div>
					{type === "users" && (
						<Select value={roleFilter} onValueChange={setRoleFilter}>
							<SelectTrigger aria-label={t("roles.title")} className="h-11 w-full sm:w-40">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t("managementHub.allRoles")}</SelectItem>
								{roles.map(role => (
									<SelectItem key={role.id} value={String(role.id)}>
										{role.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
					{type === "permissions" ? (
						<Select value={workflowGroupFilter} onValueChange={setWorkflowGroupFilter}>
							<SelectTrigger aria-label={t("permissions.title")} className="h-11 w-full sm:w-44">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t("common.all")}</SelectItem>
								{PERMISSION_GROUP_IDS.map(gid => (
									<SelectItem key={gid} value={gid}>
										{t(`permissions.groups.${gid}.title`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : (
						<Select value={statusFilter} onValueChange={setStatusFilter}>
							<SelectTrigger aria-label={t("common.status")} className="h-11 w-32">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t("common.all")}</SelectItem>
								<SelectItem value="active">{t("common.active")}</SelectItem>
								<SelectItem value="inactive">{t("common.inactive")}</SelectItem>
								{type === "users" && (
									<SelectItem value="on_leave">{t("employees.status.onLeave")}</SelectItem>
								)}
							</SelectContent>
						</Select>
					)}
					{type === "users" && (
						<PermissionGate permission="users.create">
							<Button size="sm" className="h-9" onClick={() => navigate("/users/new")}>
								<Plus className="h-4 w-4 me-1" />
								{t("employees.newEmployee")}
							</Button>
						</PermissionGate>
					)}
					{type === "roles" && (
						<PermissionGate permission="roles.create">
							<Button size="sm" className="h-9" onClick={openRoleCreate}>
								<Plus className="h-4 w-4 me-1" />
								{t("roles.actions.add")}
							</Button>
						</PermissionGate>
					)}
					{type === "departments" && (
						<PermissionGate permission="departments.create">
							<Button size="sm" className="h-9" onClick={openDepartmentCreate}>
								<Plus className="h-4 w-4 me-1" />
								{t("departments.actions.add")}
							</Button>
						</PermissionGate>
					)}
				</div>
			</div>

			{contentLoading ? (
				<Loading />
			) : (
				<>
					{/* Employees */}
					{type === "users" &&
						(filteredEmployees.length === 0 ? (
							<div className="rounded-xl border">
								{isFiltering ? (
									<EmptyState icon={Search} title={t("common.noResults")} />
								) : (
									<EmptyState
										icon={Users}
										title={t("employees.empty.title")}
										action={
											canCreateUsers
												? {
														label: t("employees.newEmployee"),
														onClick: () => navigate("/users/new"),
													}
												: undefined
										}
									/>
								)}
							</div>
						) : (
							<div className="rounded-xl border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("employees.table.employeeNumber")}</TableHead>
											<TableHead>{t("employees.table.name")}</TableHead>
											<TableHead>{t("employees.table.position")}</TableHead>
											<TableHead>{t("employees.table.branch")}</TableHead>
											<TableHead className="text-center">{t("employees.table.phone")}</TableHead>
											<TableHead className="text-center">{t("employees.table.status")}</TableHead>
											<TableHead className="text-center">{t("common.actions")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{filteredEmployees.map(emp => (
											<TableRow key={emp.id}>
												<TableCell className="font-medium">{emp.id}</TableCell>
												<TableCell>
													<div className="flex items-center gap-3">
														{emp.photo ? (
															<img
																src={emp.photo}
																alt=""
																className="h-9 w-9 rounded-full object-cover"
															/>
														) : (
															<div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
																<Users className="h-4 w-4 text-muted-foreground" />
															</div>
														)}
														<div className="min-w-0">
															<div className="font-medium truncate">{emp.fullName}</div>
															<div className="text-xs text-muted-foreground truncate">
																{visibleEmployeeEmail(emp.email) || "-"}
															</div>
														</div>
													</div>
												</TableCell>
												<TableCell>
													{emp.role ? <Badge variant="outline">{emp.role.name}</Badge> : "-"}
												</TableCell>
												<TableCell>{emp.branch?.name || "-"}</TableCell>
												<TableCell className="text-center" dir="ltr">
													{emp.phone || "-"}
												</TableCell>
												<TableCell className="text-center">
													{employeeStatusBadge(emp.status)}
												</TableCell>
												<TableCell className="text-center">
													<div className="flex items-center justify-center gap-2">
														<PermissionGate permission={["users.read", "users.update"]}>
															<Button
																title={t("common.edit")}
																aria-label={t("common.edit")}
																variant="outline"
																size="sm"
																onClick={() => navigate(`/users/${emp.id}`)}
															>
																<Edit className="h-4 w-4 text-primary" />
															</Button>
														</PermissionGate>
														<PermissionGate permission="users.delete">
															<Button
																title={t("common.delete")}
																aria-label={t("common.delete")}
																variant="outline"
																size="sm"
																onClick={() => deleteEmployee(emp.id)}
															>
																<Trash2 className="h-4 w-4 text-destructive" />
															</Button>
														</PermissionGate>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						))}

					{/* Roles */}
					{type === "roles" &&
						(filteredRoles.length === 0 ? (
							<div className="rounded-xl border">
								{isFiltering ? (
									<EmptyState icon={Search} title={t("common.noResults")} />
								) : (
									<EmptyState
										icon={Shield}
										title={t("roles.empty.title")}
										action={
											canCreateRoles
												? { label: t("roles.actions.add"), onClick: openRoleCreate }
												: undefined
										}
									/>
								)}
							</div>
						) : (
							<div className="rounded-xl border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("roles.table.headers.name")}</TableHead>
											<TableHead>{t("roles.table.headers.department")}</TableHead>
											<TableHead className="text-center">
												{t("roles.table.headers.userCount")}
											</TableHead>
											<TableHead className="text-center">
												{t("roles.table.headers.permissions")}
											</TableHead>
											<TableHead className="text-center">
												{t("roles.table.headers.status")}
											</TableHead>
											<TableHead className="text-center">{t("common.actions")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{filteredRoles.map(role => (
											<TableRow key={role.id}>
												<TableCell>
													<div className="font-medium">{role.name}</div>
													{role.nameEn && (
														<div className="text-xs text-muted-foreground">
															{role.nameEn}
														</div>
													)}
												</TableCell>
												<TableCell>{role.department?.name || "-"}</TableCell>
												<TableCell className="text-center">
													<Badge variant="outline">{role.users?.length || 0}</Badge>
												</TableCell>
												<TableCell className="text-center">
													<Button
														variant="outline"
														size="sm"
														onClick={() => openRoleDialog(role, true)}
													>
														<Eye className="h-4 w-4 me-1" />
														{role.permissions?.length || 0}
													</Button>
												</TableCell>
												<TableCell className="text-center">
													<Badge variant={role.status === "active" ? "success" : "warning"}>
														{role.status === "active"
															? t("common.active")
															: t("common.inactive")}
													</Badge>
												</TableCell>
												<TableCell className="text-center">
													<div className="flex items-center justify-center gap-2">
														<PermissionGate permission="roles.update">
															<Button
																title={t("common.edit")}
																aria-label={t("common.edit")}
																variant="outline"
																size="sm"
																onClick={() => openRoleDialog(role, false)}
															>
																<Edit className="h-4 w-4 text-primary" />
															</Button>
														</PermissionGate>
														<PermissionGate permission="roles.delete">
															<Button
																title={t("common.delete")}
																aria-label={t("common.delete")}
																variant="outline"
																size="sm"
																onClick={() => deleteRole(role.id)}
															>
																<Trash2 className="h-4 w-4 text-destructive" />
															</Button>
														</PermissionGate>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						))}

					{/* Departments */}
					{type === "departments" &&
						(filteredDepartments.length === 0 ? (
							<div className="rounded-xl border">
								{isFiltering ? (
									<EmptyState icon={Search} title={t("common.noResults")} />
								) : (
									<EmptyState
										icon={Building2}
										title={t("departments.empty.title")}
										action={
											canCreateDepartments
												? { label: t("departments.actions.add"), onClick: openDepartmentCreate }
												: undefined
										}
									/>
								)}
							</div>
						) : (
							<div className="rounded-xl border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("departments.table.headers.name")}</TableHead>
											<TableHead className="text-center">
												{t("departments.table.headers.jobCount")}
											</TableHead>
											<TableHead className="text-center">
												{t("departments.table.headers.departmentHead")}
											</TableHead>
											<TableHead className="text-center">
												{t("departments.table.headers.status")}
											</TableHead>
											<TableHead className="text-center">
												{t("departments.table.headers.createdAt")}
											</TableHead>
											<TableHead className="text-center">{t("common.actions")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{filteredDepartments.map(department => (
											<TableRow key={department.id}>
												<TableCell>
													<div className="font-medium">{department.name}</div>
													{department.description && (
														<div className="text-xs text-muted-foreground truncate max-w-[18rem]">
															{department.description}
														</div>
													)}
												</TableCell>
												<TableCell className="text-center">
													<Badge variant="outline">{department.roles?.length || 0}</Badge>
												</TableCell>
												<TableCell className="text-center">
													{department.departmentHead || "-"}
												</TableCell>
												<TableCell className="text-center">
													<Badge
														variant={department.status === "active" ? "success" : "warning"}
													>
														{department.status === "active"
															? t("common.active")
															: t("common.inactive")}
													</Badge>
												</TableCell>
												<TableCell className="text-center">
													{formatDate(department.createdAt)}
												</TableCell>
												<TableCell className="text-center">
													<div className="flex items-center justify-center gap-2">
														<PermissionGate permission="departments.update">
															<Button
																title={t("common.edit")}
																aria-label={t("common.edit")}
																variant="outline"
																size="sm"
																onClick={() => openDepartmentEdit(department)}
															>
																<Edit className="h-4 w-4 text-primary" />
															</Button>
														</PermissionGate>
														<PermissionGate permission="departments.delete">
															<Button
																title={t("common.delete")}
																aria-label={t("common.delete")}
																variant="outline"
																size="sm"
																onClick={() => deleteDepartment(department.id)}
															>
																<Trash2 className="h-4 w-4 text-destructive" />
															</Button>
														</PermissionGate>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						))}

					{/* Permission catalog (read-only reference; assignment happens inside roles) */}
					{type === "permissions" && (
						<div className="space-y-3">
							{catalogWritesAllowed === false && (
								<div
									className="flex gap-3 rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-sm text-warning dark:text-amber-100"
									role="status"
								>
									<Info
										className="mt-0.5 h-5 w-5 shrink-0 text-warning dark:text-amber-300"
										aria-hidden
									/>
									<p className="leading-relaxed">{t("permissions.errors.catalogReadOnly")}</p>
								</div>
							)}
							<p className="text-sm text-muted-foreground">{t("permissions.ui.roleAssignmentHint")}</p>
							<GroupedPermissionsPanel
								permissions={filteredPermissions}
								showSearch={false}
								scrollAreaClassName="overflow-visible overscroll-contain"
								renderExtras={p => (
									<div className="flex flex-wrap items-center justify-end gap-2">
										<span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
											<Users className="h-3 w-3 opacity-70" aria-hidden />
											{t("permissions.meta.usersWithAccess", { count: p.countUsers ?? 0 })}
										</span>
									</div>
								)}
							/>
						</div>
					)}
				</>
			)}

			{/* Role create/edit/view dialog */}
			<Dialog
				open={roleDialogOpen}
				onOpenChange={open => {
					setRoleDialogOpen(open);
					if (!open) {
						setEditingRole(null);
						setRoleReadOnly(false);
						setRoleForm({ ...EMPTY_ROLE_FORM });
					}
				}}
			>
				<DialogContent className="max-w-4xl">
					<DialogHeader>
						<DialogTitle>
							{editingRole ? t("roles.dialogs.edit.title") : t("roles.dialogs.create.title")}
						</DialogTitle>
						<DialogDescription>
							{editingRole ? t("roles.dialogs.edit.description") : t("roles.dialogs.create.description")}
						</DialogDescription>
					</DialogHeader>
					<div className="flex flex-col gap-4">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							<div className="space-y-1">
								<Label htmlFor="role-name">
									{t("roles.fields.name")} <span className="text-destructive">*</span>
								</Label>
								<Input
									id="role-name"
									readOnly={roleReadOnly}
									value={roleForm.name}
									onChange={e => setRoleForm({ ...roleForm, name: e.target.value })}
									placeholder={t("roles.fields.namePlaceholder")}
								/>
							</div>

							<div className="space-y-1">
								<Label htmlFor="role-status">{t("roles.fields.status")}</Label>
								<Select
									disabled={roleReadOnly}
									value={roleForm.status}
									onValueChange={(value: "active" | "inactive") =>
										setRoleForm({ ...roleForm, status: value })
									}
								>
									<SelectTrigger aria-label={t("roles.fields.status")}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="active">{t("common.active")}</SelectItem>
										<SelectItem value="inactive">{t("common.inactive")}</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-1">
								<Label htmlFor="role-departmentId">{t("roles.fields.department")}</Label>
								<Select
									disabled={roleReadOnly}
									value={roleForm.departmentId != null ? String(roleForm.departmentId) : "none"}
									onValueChange={value =>
										setRoleForm(prev => ({
											...prev,
											departmentId: value === "none" ? undefined : value,
										}))
									}
								>
									<SelectTrigger aria-label={t("roles.fields.department")}>
										<SelectValue placeholder={t("roles.fields.departmentPlaceholder")} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="none">{t("common.none")}</SelectItem>
										{departments.map(dept => (
											<SelectItem key={dept.id} value={String(dept.id)}>
												{dept.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<details open className="border rounded-lg">
							<summary className="p-3 cursor-pointer text-sm min-h-11">
								{t("workspace.roleDetails")}
							</summary>
							<div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
								<div className="space-y-1">
									<Label htmlFor="role-nameEn">{t("roles.fields.nameEn")}</Label>
									<Input
										id="role-nameEn"
										readOnly={roleReadOnly}
										value={roleForm.nameEn}
										onChange={e => setRoleForm({ ...roleForm, nameEn: e.target.value })}
										placeholder={t("roles.fields.nameEnPlaceholder")}
									/>
								</div>
								<div className="space-y-1">
									<Label htmlFor="role-description">{t("roles.fields.description")}</Label>
									<Input
										id="role-description"
										readOnly={roleReadOnly}
										value={roleForm.description}
										onChange={e => setRoleForm({ ...roleForm, description: e.target.value })}
										placeholder={t("roles.fields.descriptionPlaceholder")}
									/>
								</div>
								<div className="space-y-1">
									<Label htmlFor="role-descriptionEn">{t("roles.fields.descriptionEn")}</Label>
									<Input
										id="role-descriptionEn"
										readOnly={roleReadOnly}
										value={roleForm.descriptionEn}
										onChange={e => setRoleForm({ ...roleForm, descriptionEn: e.target.value })}
										placeholder={t("roles.fields.descriptionEnPlaceholder")}
									/>
								</div>
							</div>
						</details>
						<div className="w-full space-y-2">
							<div>
								<Label>{t("roles.fields.permissions")}</Label>
								<p className="text-sm text-muted-foreground mt-1">
									{t("permissions.ui.roleAssignmentHint")}
								</p>
							</div>
							{permissions.length === 0 ? (
								<p className="text-sm text-muted-foreground border rounded-md p-4">
									{t("roles.fields.noPermissionsAvailable")}
								</p>
							) : roleReadOnly ? (
								<GroupedPermissionsPanel
									permissions={permissions}
									idPrefix="view-perm"
									scrollAreaClassName="overflow-visible overscroll-contain"
									grantOverlay={{
										grantedIds: new Set((editingRole?.permissions ?? []).map(p => String(p.id))),
									}}
								/>
							) : (
								<GroupedPermissionsPanel
									permissions={permissions}
									idPrefix="edit-perm"
									scrollAreaClassName="overflow-visible overscroll-contain"
									selection={{
										selectedIds: (roleForm.permissionIds ?? []).map(String),
										onToggle: (permissionId, checked) => {
											const currentIds = roleForm.permissionIds ?? [];
											if (checked) {
												setRoleForm({
													...roleForm,
													permissionIds: [...currentIds, permissionId],
												});
											} else {
												setRoleForm({
													...roleForm,
													permissionIds: currentIds.filter(id => String(id) !== permissionId),
												});
											}
										},
									}}
								/>
							)}
						</div>
					</div>
					<DialogFooter className="sticky -bottom-5 bg-card py-3 z-10">
						<Button
							variant="outline"
							onClick={() => {
								setRoleDialogOpen(false);
								setRoleReadOnly(false);
							}}
						>
							{t("common.cancel")}
						</Button>
						{!roleReadOnly && (
							<Button onClick={saveRole} disabled={roleSaving || !roleForm.name.trim()}>
								{roleSaving
									? t("common.saving")
									: editingRole
										? t("common.update")
										: t("common.create")}
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Department create/edit dialog */}
			<Dialog
				open={departmentDialogOpen}
				onOpenChange={open => {
					setDepartmentDialogOpen(open);
					if (!open) {
						setEditingDepartment(null);
						setDepartmentForm({ ...EMPTY_DEPARTMENT_FORM });
					}
				}}
			>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							{editingDepartment
								? t("departments.dialogs.edit.title")
								: t("departments.dialogs.create.title")}
						</DialogTitle>
						<DialogDescription>
							{editingDepartment
								? t("departments.dialogs.edit.description")
								: t("departments.dialogs.create.description")}
						</DialogDescription>
					</DialogHeader>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="flex flex-col gap-2">
							<Label htmlFor="dept-name">
								{t("departments.fields.name")} <span className="text-destructive">*</span>
							</Label>
							<Input
								id="dept-name"
								value={departmentForm.name}
								onChange={e => setDepartmentForm({ ...departmentForm, name: e.target.value })}
								placeholder={t("departments.fields.namePlaceholder")}
							/>
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="dept-status">{t("departments.fields.status")}</Label>
							<Select
								value={departmentForm.status}
								onValueChange={(value: "active" | "inactive") =>
									setDepartmentForm({ ...departmentForm, status: value })
								}
							>
								<SelectTrigger aria-label={t("departments.fields.status")}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="active">{t("common.active")}</SelectItem>
									<SelectItem value="inactive">{t("common.inactive")}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="md:col-span-2 flex flex-col gap-2">
							<Label htmlFor="dept-head">{t("departments.fields.departmentHead")}</Label>
							<Input
								id="dept-head"
								value={departmentForm.departmentHead}
								onChange={e => setDepartmentForm({ ...departmentForm, departmentHead: e.target.value })}
								placeholder={t("departments.fields.departmentHeadPlaceholder")}
							/>
						</div>
						<div className="md:col-span-2 flex flex-col gap-2">
							<Label htmlFor="dept-description">{t("departments.fields.description")}</Label>
							<Input
								id="dept-description"
								value={departmentForm.description}
								onChange={e => setDepartmentForm({ ...departmentForm, description: e.target.value })}
								placeholder={t("departments.fields.descriptionPlaceholder")}
							/>
						</div>
					</div>
					<DialogFooter className="sticky -bottom-5 bg-card py-3 z-10">
						<Button variant="outline" onClick={() => setDepartmentDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={saveDepartment} disabled={departmentSaving || !departmentForm.name.trim()}>
							{departmentSaving
								? t("common.saving")
								: editingDepartment
									? t("common.update")
									: t("common.create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
